import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { ITEMS, type ItemId } from '@goodtime/shared';
import { AssetLoader } from '../../asset/AssetLoader';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import type { InfoDialogTab } from './PaytableDisplay';

interface Section {
  title: string;
  subtitle: string;
  items: ReadonlyArray<ItemId>;
}

const SECTIONS: ReadonlyArray<Section> = [
  { title: 'YOUR ITEMS', subtitle: 'Boost your own pot', items: ['BOOST', 'COIN_STASH', 'TRIPLE_BOOST', 'X2_BONUS', 'MAGNET', 'SHIELD'] },
  { title: 'RIVAL ITEMS', subtitle: 'Hit, halve or steal from rivals', items: ['HIT', 'BOMB', 'LIGHTNING', 'SHAKEDOWN'] },
  { title: 'WHEEL BONUS', subtitle: 'Shared: anyone can land on it', items: ['BONUS_WEDGE'] },
];

interface Row {
  itemId: ItemId;
  icon: Sprite;
  glyph: Text;
  name: Text;
  desc: Text;
}

interface SectionView {
  title: Text;
  subtitle: Text;
  rows: Row[];
}

/** Room above and below the board: its plaque rides half above it. */
const MARGIN = 19;

/**
 * The Reel Race's item guide, the info board's second tab, on a board like
 * the paytable (producer 2026-10-14: no plain dialogs): each item with its
 * icon, name and what it does, in three groups. It fits itself to the
 * screen; tapping the dim closes it.
 */
export class ItemsCatalogDisplay extends BoardDialog {
  private readonly sections: SectionView[] = [];
  private readonly rules = new Graphics();
  private readonly closeButton: BrassButton;
  private readonly tabBar = new Container();
  private tabButtons: BrassButton[] = [];
  private isOpen = false;
  private screenH = 1;
  private arranging = false;
  /** Fires when the player closes it (the dim or CLOSE). */
  onCloseRequested: (() => void) | null = null;

  constructor(private readonly floor: () => Texture | null = () => null) {
    super({ title: 'ITEMS', floor: floor(), maxWidth: 540, titleSize: 18 });
    this.closeButton = new BrassButton({ label: 'CLOSE', kind: 'wood', size: 16, onTap: () => this.requestClose() });
    this.content.addChild(this.tabBar, this.rules);
    for (const section of SECTIONS) {
      const title = new Text({ text: section.title, style: displayStyle(15) });
      const subtitle = new Text({ text: section.subtitle, style: uiStyle(12, COLOURS.cream, '500') });
      title.anchor.set(0, 0.5);
      subtitle.anchor.set(0, 0.5);
      this.content.addChild(title, subtitle);
      const rows = section.items.map((itemId) => {
        const meta = ITEMS[itemId];
        const row: Row = {
          itemId,
          icon: new Sprite(),
          glyph: new Text({ text: meta.glyph, style: { fontSize: 24 } }),
          name: new Text({ text: meta.name, style: displayStyle(14) }),
          desc: new Text({ text: plainDashes(meta.description), style: { ...uiStyle(12, COLOURS.cream, '500'), wordWrap: true } }),
        };
        row.icon.anchor.set(0.5);
        row.glyph.anchor.set(0.5);
        row.name.anchor.set(0, 0);
        row.desc.anchor.set(0, 0);
        this.content.addChild(row.icon, row.glyph, row.name, row.desc);
        return row;
      });
      this.sections.push({ title, subtitle, rows });
    }
    this.content.addChild(this.closeButton);
    this.onDimTap = () => this.requestClose();
  }

  setTabs(tabs: ReadonlyArray<InfoDialogTab> | null): void {
    for (const b of this.tabButtons) b.destroy({ children: true });
    this.tabButtons = (tabs ?? []).map(
      (tab) => new BrassButton({ label: tab.label, kind: tab.active ? 'gold' : 'wood', size: 15, onTap: tab.onTap }),
    );
    this.tabBar.addChild(...this.tabButtons);
    this.arrange();
  }

  layout(viewWidth: number, viewHeight: number): void {
    this.screenH = viewHeight;
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  show(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setFloor(this.floor());
    this.refreshIcons();
    this.arrange();
    void this.open();
  }

  hide(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    void this.close();
  }

  toggle(): void {
    if (this.isOpen) this.hide();
    else this.show();
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  /** Kept for the scene's teardown. */
  destroyCatalog(): void {
    this.isOpen = false;
  }

  private requestClose(): void {
    if (!this.isOpen) return;
    this.hide();
    this.onCloseRequested?.();
  }

  /** Item icons register with the theme; use them once they've loaded. */
  private refreshIcons(): void {
    for (const section of this.sections) {
      for (const row of section.rows) {
        const texture = AssetLoader.shared.getTexture(`item:${row.itemId}`);
        if (texture) row.icon.texture = texture;
        row.icon.visible = texture !== null;
        row.glyph.visible = texture === null;
      }
    }
  }

  private arrange(): void {
    if (this.arranging || !this.closeButton) return;
    this.arranging = true;
    const w = this.contentWidth;
    let y = 0;
    if (this.tabButtons.length > 0) {
      const gap = 8;
      const each = Math.floor((w - gap * (this.tabButtons.length - 1)) / this.tabButtons.length);
      this.tabButtons.forEach((b, i) => {
        b.resize(each, 38);
        b.position.set(i * (each + gap), 0);
      });
      y = 46;
    }
    // Each row as tall as its words; the type and spacing step down until
    // it all fits below the wallet bar.
    const avail = Math.max(240, this.screenH - this.topInset - MARGIN * 2 - 48) - y - 10 - 46;
    const steps = [
      { name: 14, desc: 12, pad: 6, head: 28, icon: 30 },
      { name: 13, desc: 11, pad: 4, head: 26, icon: 28 },
      { name: 12, desc: 11, pad: 2, head: 24, icon: 26 },
      { name: 12, desc: 10, pad: 1, head: 22, icon: 24 },
    ];
    let step = steps[0]!;
    const rowH = (row: Row) => Math.max(step.icon + step.pad, row.name.height + row.desc.height + step.pad);
    const measure = (): number => {
      let total = this.sections.length * step.head;
      for (const section of this.sections) {
        for (const row of section.rows) {
          row.name.style.fontSize = step.name;
          row.desc.style.fontSize = step.desc;
          row.desc.style.wordWrapWidth = w - step.icon - 12;
          total += rowH(row);
        }
      }
      return total;
    };
    for (const next of steps) {
      step = next;
      if (measure() <= avail) break;
    }
    const headH = step.head;
    const icon = step.icon;
    const textX = icon + 12;
    const lines = this.rules.clear();
    for (const section of this.sections) {
      section.title.position.set(0, y + headH / 2);
      section.subtitle.position.set(section.title.width + 10, y + headH / 2 + 1);
      y += headH;
      lines.moveTo(0, y).lineTo(w, y).stroke({ color: COLOURS.brass, alpha: 0.45, width: 1 });
      for (const row of section.rows) {
        const h = rowH(row);
        const cy = y + h / 2;
        const k = icon / Math.max(1, row.icon.texture.width, row.icon.texture.height);
        row.icon.scale.set(k);
        row.icon.position.set(icon / 2 + 2, cy);
        row.glyph.position.set(icon / 2 + 2, cy);
        row.name.position.set(textX, y + step.pad / 2);
        row.desc.position.set(textX, y + step.pad / 2 + row.name.height);
        y += h;
        lines.moveTo(0, y).lineTo(w, y).stroke({ color: COLOURS.brass, alpha: 0.18, width: 1 });
      }
    }
    y += 10;
    const bw = Math.min(w, 200);
    this.closeButton.resize(bw, 44);
    this.closeButton.position.set((w - bw) / 2, y);
    y += 46;
    this.setContentHeight(y);
    this.arranging = false;
  }
}

/** The item list's words without dashes (the game uses none): "appears
 *  on the wheel: anyone can land on it". */
function plainDashes(text: string): string {
  return text.replace(/\s*[\u2014\u2013]\s*/g, ': ');
}
